'use client';
import { Badge, Button, Modal, Select, Tooltip } from '@ethsign/ui';

import { useQuery } from '@tanstack/react-query';

import React, { ReactNode, useState } from 'react';

import { CheckVerifiedIconBig } from '@/components';
import { HtmlEditor } from '@/components/HtmlEditor';
import { Spin } from '@/components/Loading';
import { queryContractTemplate } from '@/services';
import { ITemplate } from '@/types';

type Props = {
  data: any;
  trigger: ReactNode;
  handleUse: (data: ITemplate) => void;
  service?: (id: string) => Promise<any>;
};

const tagsColor: any = {
  Fundraising: 'text-blue-700 bg-blue-50',
  Employment: 'text-primary-700 bg-primary-50',
  'Business Transaction': 'text-cyan-700 bg-cyan-50',
  'Intellectual Property': 'text-secondary-700 bg-secondary-50',
  'Entity Mapping ': 'text-darkMatter-700 bg-darkMatter-50',
  'Entity Setup': 'text-teal-700 bg-teal-50',
  'Business Operations': 'text-darkMatter-700 bg-darkMatter-50',
  'Company Administration': 'text-teal-700 bg-teal-50'
};

export const ContractTypeTag = ({ type }: { type: string }) => {
  return <Badge className={`rounded ${tagsColor[type]}`}>{type}</Badge>;
};

export function TemplatePreviewModal(props: Props) {
  const [open, setOpen] = useState(false);
  const { data, trigger, handleUse, service } = props;
  const isCollection = data?.type === 'collection';
  const [templateId, setTemplateId] = useState(
    isCollection ? String(data.templates?.[0]?.templateId) : String(data.templateId || data.id)
  );
  const { data: templateDetail, isLoading } = useQuery({
    queryKey: ['contract-template', templateId],
    queryFn: () => (service ? service(templateId) : queryContractTemplate(templateId)),
    enabled: !!templateId && open
  });
  const { status, description, applicableJurisdictions, categoryName, providerName } = templateDetail || {};
  return (
    <Modal className={'md:w-[1180px] max-w-none'} footer={false} open={open} onOpenChange={setOpen} trigger={trigger}>
      <div
        className="h-full w-full overflow-auto"
        onScroll={(e) => {
          e.stopPropagation();
        }}
      >
        <Spin loading={isLoading}>
          <div className="!max-w-[1180px]  px-0 max-h-screen  flex flex-col bg-white ">
            <div className="flex flex-row justify-between items-center px-4 sm:px-10 flex-wrap ">
              <div>
                <div className="text-lg sm:text-xxl font-medium text-gray-900 flex flex-row gap-2 items-center ">
                  {data.name}
                  {status === 'verified' && (
                    <span>
                      <CheckVerifiedIconBig />
                    </span>
                  )}
                  {data?.type === 'collection' && (
                    <Select
                      value={templateId}
                      onChange={setTemplateId}
                      options={
                        data?.templates?.map((it: any) => ({
                          value: String(it.templateId),
                          label: it.name
                        })) || []
                      }
                    />
                  )}
                </div>
                <Tooltip
                  className="border"
                  side="bottom"
                  content={
                    <div className="text-sm font-medium text-gray-500 w-full mt-3 sm:mt-4 max-w-[880px]">
                      {description}
                    </div>
                  }
                >
                  <div className="text-sm font-medium text-gray-500 w-full mt-3 sm:mt-4 max-w-[880px] line-clamp-3 cursor-pointer">
                    {description}
                  </div>
                </Tooltip>
              </div>
              <Button onClick={() => handleUse(templateDetail)} className={`mt-3 lg:mt-0 p-2`}>
                Use this Template
              </Button>
            </div>
            <div className="min-h-[500px] h-[500px] mt-6 overflow-auto mx-4 sm:mx-0 border sm:border-0 rounded-md sm:rounded-none mb-10 sm:mb-0">
              <HtmlEditor type={'preview'} initData={templateDetail?.contentHtml} />
            </div>
            <div>
              <div className="border-t sm:border-t-0 pt-6 sm:pt-8 flex flex-col sm:flex-row gap-5 justify-start items-center sm:items-center px-4 sm:px-10 flex-wrap">
                <div className="text-sm font-normal text-gray-500 mr-0 sm:mr-8 ">
                  Contract Type: <ContractTypeTag type={categoryName} />
                </div>
                <div className="text-sm font-normal text-gray-500 sm:px-10">
                  Applicable Jurisdictions: <span className="font-medium text-gray-800">{applicableJurisdictions}</span>
                </div>
                <div className="text-xs font-normal text-gray-400">
                  Created by:
                  <a href={''} target="_blank" rel="noreferrer">
                    <span className="font-medium text-gray-600"> {providerName}</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </Spin>
      </div>
    </Modal>
  );
}
